import { AnimatedButton } from '@ts/components/AnimatedButton';
import { Modal } from '@ts/components/Modal';
import htmx from 'htmx.org';

let pendingPersonalizationSwap = null;
export let personalizationData = [];

export const init = () => {
    setHTMXListeners();
    setInteractiveElements();
};

const setInteractiveElements = () => {
    setImageSelects();
    setPreviewButton();
    setViewAllCartPersonalizations();
}

const setPreviewButton = () => {
    const previewButton = AnimatedButton.getButton('preview-personalizations');
    const saveButton = AnimatedButton.getButton('save-personalizations');
    if (!previewButton || !saveButton) return;
    previewButton.setOnClick((e, btn) => {
        saveButton.disable();
    });
}

const setHTMXListeners = () => {
    document.body.addEventListener('htmx:afterSettle', (evt) => { 
        setInteractiveElements(); 
    });
    document.body.addEventListener('htmx:historyRestore', setInteractiveElements);
    // Check for product personalization form submission to trigger success actions
    document.body.addEventListener('htmx:beforeSwap', handlePersonalizationSave);
}

const handlePersonalizationSave = (evt) => {
   
    const triggerId = evt.detail?.requestConfig?.elt?.id;
    if (!triggerId) return;

    const isPersonalizationForm = triggerId === 'save-personalizations';
    if (!isPersonalizationForm) return;
    
    const form = evt.detail.elt.querySelector('form');
    if (!form) return;

    const source = form.dataset.source;
    
    const xhr = evt.detail.xhr;
    if (xhr.status < 200 || xhr.status >= 300) return;

    const validationStatus = xhr.getResponseHeader('x-validation-status');
    if (validationStatus?.toLowerCase() !== 'true') return;
    pendingPersonalizationSwap = {
        target: evt.detail.target,
        content: xhr.responseText,
    };
    evt.detail.shouldSwap = false;
    personalizeOnSuccess(source);
    return;
};

const removeHtmxAttributes = (element) => {
    pendingPersonalizationSwap.swapStyle = element.hxSwap;
    element.removeAttribute('hx-trigger');
    element.removeAttribute('hx-swap');
    element.removeAttribute('hx-target');
    element.removeAttribute('hx-get');
    element.removeAttribute('hx-post');
    element.addEventListener('htmx:beforeRequest', function(evt) {
        evt.preventDefault();
    }, { once: true });
}   

const gatherPersonalizationData = async () => {
    const personalizationElements = document.querySelectorAll('.custom-personalization-option');
    const data = {};
    personalizationElements.forEach(element => {
        const personalizationId = element.getAttribute('data-personalization-id');
        data[personalizationId] = element.value;
    });
    return data;
}

const updateProductFormWithPersonalizationData = async () => {
    const data = await gatherPersonalizationData();
    personalizationData = Object.entries(data).map(([personalizationId, value]) => ({
        id: personalizationId,
        value: value
    }));
};

const handleCartPersonalizationClose = () => {
    const panel = document.getElementById('cart-panel');
    if (panel?.classList.contains('cart-panel--open')) {
        htmx.trigger(document.body, 'refresh-cart');
    }
}

const setViewAllCartPersonalizations = () => {
    const viewAllButtons = document.querySelectorAll('.view-more-personalizations');
    viewAllButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            const optionsContainer = button.closest('.cart-item-details').querySelector('.personalization-options');
            if (optionsContainer) {
                optionsContainer.classList.toggle('expanded');
                button.textContent = optionsContainer.classList.contains('expanded') ? 'View Less' : 'View All';
            }
        });
    });
}

const personalizeOnSuccess = async (source) => {
    await updateProductFormWithPersonalizationData();

    const modal = Modal.getModal('personalize-product-modal');
    modal.setOnClose(() => {
        runPendingPersonalizationSwap();
        if (source == 'cart') handleCartPersonalizationClose();
    });

    modal.hide();

    const chooseOptionsBtn = document.getElementById('personalized-button');
    if (!chooseOptionsBtn) return;

    removeHtmxAttributes(chooseOptionsBtn);
    chooseOptionsBtn.textContent = chooseOptionsBtn.getAttribute('data-saved-options-label') || 'Edit Options';
    const productElements = document.getElementById('product-elements');
    if (productElements) {
        productElements.hidden = false;
    }
}

const runPendingPersonalizationSwap = () => {
    if (!pendingPersonalizationSwap) return;
    htmx.swap(
        pendingPersonalizationSwap.target,
        pendingPersonalizationSwap.content,
        { swapStyle: pendingPersonalizationSwap.swapStyle }
    );

    pendingPersonalizationSwap = null;
};

const setImageSelects = () => {
    const selects = document.querySelectorAll('select.image-select');

    for (const select of selects) {
        if (select.dataset.imageSelectEnhanced === 'true') {
            continue;
        }

        const options = Array.from(select.options)
            .filter((option) => option.value !== '');

        if (options.length === 0 || options.every((option) => !option.dataset.iconurl)) {
            continue;
        }

        select.dataset.imageSelectEnhanced = 'true';
        select.classList.add('image-select-native');

        const wrapper = document.createElement('div');
        wrapper.className = 'image-select-enhanced';

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'image-select-trigger form-select';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-label', `Choose ${select.id || 'option'}`);

        const list = document.createElement('ul');
        list.className = 'image-select-list';
        list.setAttribute('role', 'listbox');
        list.hidden = true;

        const updateTrigger = () => {
            const selectedOption = select.options[select.selectedIndex] || options[0];
            const iconUrl = selectedOption?.dataset.iconurl;
            const text = selectedOption?.textContent?.trim() ?? '';

            trigger.textContent = '';

            if (iconUrl) {
                const icon = document.createElement('img');
                icon.src = iconUrl;
                icon.alt = '';
                icon.className = 'image-select-thumb';
                icon.addEventListener('error', () => {
                    icon.src = '/assets/img/shop/personalizedProducts/blank.png';
                });
                trigger.append(icon);
            }

            const label = document.createElement('span');
            label.className = 'image-select-label';
            label.textContent = text;
            trigger.append(label);
        };

        const closeList = () => {
            if (list.hidden) return;
            trigger.setAttribute('aria-expanded', 'false');
            list.hidden = true;
            resizeModal();
        };

        const openList = () => {
            if (!list.hidden) return;
            trigger.setAttribute('aria-expanded', 'true');
            list.hidden = false;
            resizeModal();
        };

        const resizeModal = () => {
            const listHeight = list.offsetHeight;
            const listContainer = list.closest('.form-group') || list.parentElement;

            if (list.hidden) {
                listContainer.style.height = 'auto';
            } else {
                listContainer.style.height = `${listContainer.offsetHeight + listHeight}px`;
            }
        };

        const toggleList = () => {
            if (list.hidden) {
                openList();
                const selected = list.querySelector('[aria-selected="true"]');
                selected?.focus();
                return;
            }

            closeList();
        };

        for (const option of options) {
            const item = document.createElement('li');
            item.className = 'image-select-option';
            item.setAttribute('role', 'option');
            item.tabIndex = -1;
            item.dataset.value = option.value;

            if (option.value === select.value) {
                item.setAttribute('aria-selected', 'true');
                item.classList.add('selected');
            }
            else {
                item.setAttribute('aria-selected', 'false');
            }

            if (option.dataset.iconurl) {
                const icon = document.createElement('img');
                icon.src = option.dataset.iconurl;
                icon.alt = '';
                icon.className = 'image-select-thumb';
                icon.addEventListener('error', () => {
                    icon.src = '/assets/img/shop/personalizedProducts/blank.png';
                });
                item.append(icon);
            }

            const label = document.createElement('span');
            label.className = 'image-select-label';
            label.textContent = option.textContent?.trim() ?? '';
            item.append(label);

            item.addEventListener('click', () => {
                if (select.value !== option.value) {
                    select.value = option.value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }

                for (const optionItem of list.querySelectorAll('.image-select-option')) {
                    const isSelected = optionItem.dataset.value === option.value;
                    optionItem.setAttribute('aria-selected', isSelected ? 'true' : 'false');
                    optionItem.classList.toggle('selected', isSelected);
                }

                updateTrigger();
                closeList();
                trigger.focus();
            });

            item.addEventListener('keydown', (event) => {
                const optionItems = Array.from(list.querySelectorAll('.image-select-option'));
                const currentIndex = optionItems.indexOf(item);

                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    const next = optionItems[(currentIndex + 1) % optionItems.length];
                    next?.focus();
                }

                if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    const previous = optionItems[(currentIndex - 1 + optionItems.length) % optionItems.length];
                    previous?.focus();
                }

                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    item.click();
                }

                if (event.key === 'Escape') {
                    event.preventDefault();
                    closeList();
                    trigger.focus();
                }
            });

            list.append(item);
        }

        trigger.addEventListener('click', () => {
            toggleList();
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                openList();

                const selected = list.querySelector('[aria-selected="true"]');
                selected?.focus();
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                closeList();
            }
        });

        document.addEventListener('click', (event) => {
            if (!(event.target instanceof Node)) {
                return;
            }

            if (!wrapper.contains(event.target)) {
                closeList();
            }
        });

        select.addEventListener('change', () => {
            for (const optionItem of list.querySelectorAll('.image-select-option')) {
                const isSelected = optionItem.dataset.value === select.value;
                optionItem.setAttribute('aria-selected', isSelected ? 'true' : 'false');
                optionItem.classList.toggle('selected', isSelected);
            }

            updateTrigger();
        });

        updateTrigger();

        select.insertAdjacentElement('afterend', wrapper);
        wrapper.append(trigger, list);
    }
}